import React from 'react'
import Image from 'gatsby-image'
import styled, { css } from 'styled-components'
import { themePx, themePxNeg } from '../styles/theme'
import { themeGet } from '@styled-system/theme-get'
import { up, down } from '@thomaseckhardt/styled-breakpoints'
import Caption from '../components2/Caption'

export const VARIANT_PORTRAIT_LANDSCAPE_INTENDED = 'variant-portrait-landscape-intended'
export const VARIANT_FOURTHIRD_DUO_QUADRAT_INTENDED = 'variant-fourthird-duo-quadrat'
export const VARIANT_PORTRAIT_DUO_INTENDED = 'variant-portrait-duo-intended'
export const VARIANT_TRIO = 'variant-trio'
export const VARIANT_DUO = 'variant-duo'

const Root = styled.div`
  /* VARIANT_PORTRAIT_LANDSCAPE_INTENDED */
  ${props => (props.variant === VARIANT_PORTRAIT_LANDSCAPE_INTENDED && css`
    display: grid;
    grid-template-columns: repeat(16, 1fr);
    grid-template-rows: max-content;

    > ${Item}:nth-child(odd) {
      grid-column: 1 / span 11;
      z-index: 1;
    }

    > ${Item}:nth-child(even) {
      grid-column: 2 / span 15;
      margin-top: ${props => themeGet('space.3')(props) * -1}px;
    }

    ${up('tablet')} {
      > ${Item}:nth-child(odd) {
        grid-column: 2 / span 6;
        grid-row: 1 / span 2;
        transform: translateX(-24px);
      }

      > ${Item}:nth-child(even) {
        grid-column: 8 / span 9;
        grid-row-start: 1;
        margin-top: 0;
      }
    }

    ${up('laptop')} {
      > ${Item}:nth-child(odd) {
        grid-column: 5 / span 5;
      }

      > ${Item}:nth-child(even) {
        grid-column: 10 / span 7;
      }
    }
  `)}

  /* VARIANT_FOURTHIRD_DUO_QUADRAT_INTENDED */
  ${props => (props.variant === VARIANT_FOURTHIRD_DUO_QUADRAT_INTENDED && css`
    display: grid;
    grid-template-columns: repeat(16, 1fr);
    grid-template-rows: max-content;

    > ${Item}:nth-child(1) {
      grid-column: 1 / span 9;
      z-index: 1;
    }

    > ${Item}:nth-child(2) {
      grid-column: 10 / -2;
      align-self: end;
    }

    > ${Item}:nth-child(3) {
      grid-column: 2 / -1;
    }

    ${up('tablet')} {
      > ${Item}:nth-child(1) {
        grid-column: 2 / span 7;
        grid-row: 1 / span 2;
        align-self: end;
        transform: translateX(${themePxNeg('space.3')});
      }

      > ${Item}:nth-child(2) {
        grid-column: 9 / -4;
        grid-row: 1 / span 1;
      }

      > ${Item}:nth-child(3) {
        grid-column: 9 / -1;
        grid-row: 2 / span 2;
        margin-top: ${themePx('space.3')};
      }
    }

    ${up('laptop')} {
      > ${Item}:nth-child(1) {
        grid-column: 5 / span 5;
        grid-row: 1 / span 2;
      }

      > ${Item}:nth-child(2) {
        grid-column: 10 / span 3;
      }

      > ${Item}:nth-child(3) {
        grid-column: 10 / -1;
      }
    }
  `)}

  /* VARIANT_PORTRAIT_DUO_INTENDED */
  ${props => (props.variant === VARIANT_PORTRAIT_DUO_INTENDED && css`
    display: grid;
    grid-template-columns: repeat(16, 1fr);
    grid-template-rows: max-content;

    > ${Item}:nth-child(1) {
      grid-column: 2 / span 7;
      transform: translateX(${themePxNeg('space.3')});
    }

    > ${Item}:nth-child(2) {
      grid-column: 9 / span 7;
    }

    ${up('laptop')} {
      > ${Item}:nth-child(1) {
        grid-column: 5 / span 5;
      }

      > ${Item}:nth-child(2) {
        grid-column: 10 / -3;
      }
    }
  `)}

  /* VARIANT_TRIO */
  ${props => (props.variant === VARIANT_TRIO && css`
    padding: 0 ${1 / 16 * 100}%;

    ${down('tablet')} {
      > ${Item} {
        &:not(:first-of-type) {
          margin-top: ${props => props.theme.px('space.1')};
        }
      }
    }

    ${up('tablet')} {
      display: flex;

      > ${Item} {
        width: 33%;

        &:not(:first-of-type) {
          margin-left: ${props => props.theme.px('space.1')};
        }
      }
    }
  `)}

  /* VARIANT_DUO */
  ${props => (props.variant === VARIANT_DUO && css`
    padding: 0 ${1 / 16 * 100}%;

    ${down('tablet')} {
      > ${Item} {
        &:not(:first-of-type) {
          margin-top: ${props => props.theme.px('space.1')};
        }
      }
    }

    ${up('tablet')} {
      display: flex;

      > ${Item} {
        width: 50%;

        &:not(:first-of-type) {
          margin-left: ${props => props.theme.px('space.1')};
        }
      }
    }
  `)}

  /* Default variant */
  ${props => (props.variant === undefined && css`
    > *:nth-child(1) {
      grid-column: 5 / span 4;
      grid-row-start: 1;
      transform: translateX(-24px);
    }

    > *:nth-child(2) {
      grid-column: 9 / span 4;
      grid-row-start: 1;
    }

    > *:nth-child(3) {
      grid-column: 5 / span 8;
      grid-row-start: 2;
      padding-right: ${props => props.theme.px('space.3')};
    }
  `)}
`

const Item = styled.div`
  position: relative;
`

const StyledCaption = styled(Caption)`
  grid-column: 2 / -2;
  max-width: 336px;
  padding-top: ${props => props.theme.px('space.1')(props)};

  ${up('tablet')} {
    grid-column: 8 / -1;
    grid-row-start: 2;
  }

  ${up('laptop')} {
    grid-column: 10 / -1;
  }

  ${props => (props.variant === VARIANT_FOURTHIRD_DUO_QUADRAT_INTENDED && css`
  padding-top: ${themePx('space.2')};

    ${up('tablet')} {
      grid-column: 3 / span 5;
      grid-row-start: 3;
    }

    ${up('laptop')} {
      grid-column: 5 / span 5;
      grid-row-start: 3;
    }
  `)}

  ${props => (props.variant === VARIANT_PORTRAIT_DUO_INTENDED && css`
    padding-top: ${themePx('space.2')};

    ${up('tablet')} {
      grid-column: 3 / -3;
    }

    ${up('laptop')} {
      grid-column: 5 / -3;
    }
  `)}
`

const Puzzle = props => {
  const { variant, items, caption, ...rest } = props

  const itemElems = []
  items.forEach((item, index) => {
    itemElems.push(<Item key={index}><Image backgroundColor={'#f5f5f5'} {...item} /></Item>)
  })

  return (
    <Root variant={variant} {...rest}>
      {itemElems}
      {caption && 
        <StyledCaption variant={variant}>
          {caption}
        </StyledCaption>
      }
    </Root>
  )
}

Puzzle.defaultProps = {
  variant: VARIANT_PORTRAIT_LANDSCAPE_INTENDED
}

export default Puzzle
